import React, { useState } from "react";
import { Share2, Download, X, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const W = 1080;
const H = 1350;

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawCard = async ({ score, total, marks, dateLabel, streak, label, champion }) => {
  await Promise.all([
    document.fonts.load("700 160px Inter"),
    document.fonts.load("600 40px Inter"),
  ]).catch(() => {});
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#2D4A3E"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = "600 30px Inter";
  ctx.letterSpacing = "8px";
  ctx.fillText(`CURIOVAULT  ·  ${label.toUpperCase()}`, 90, 150);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 76px Inter, sans-serif";
  ctx.fillText(dateLabel, 90, 260);
  roundRect(ctx, 90, 340, W - 180, 640, 48);
  ctx.fillStyle = "#3E5D50"; ctx.fill();
  ctx.strokeStyle = "#557367"; ctx.lineWidth = 3; ctx.stroke();
  const ms = [100, 30, 7].find((m) => streak >= m);
  if (ms) {
    const seal = await new Promise((res) => { const im = new Image(); im.onload = () => res(im); im.onerror = () => res(null); im.src = "/seal-black-256.png"; });
    if (seal) {
      ctx.save(); ctx.beginPath(); ctx.arc(W - 190, 190, 120, 0, Math.PI * 2); ctx.fillStyle = "#F4F1EA"; ctx.fill(); ctx.restore();
      ctx.drawImage(seal, W - 290, 90, 200, 200);
    }
    ctx.fillStyle = "#FFFFFF"; ctx.font = "700 30px Inter"; ctx.textAlign = "center";
    ctx.fillText(`${ms}-DAY SEAL`, W - 190, 328); ctx.textAlign = "left";
  }
  if (streak > 1) {
    roundRect(ctx, W - 90 - 360, 380, 300, 84, 42);
    ctx.fillStyle = "#E5EEE8"; ctx.fill();
    ctx.fillStyle = "#2D4A3E"; ctx.font = "700 40px Inter";
    ctx.fillText(`${streak}-day streak`, W - 90 - 330, 436);
  }
  if (champion) {
    roundRect(ctx, 150, 380, 440, 84, 42);
    ctx.fillStyle = "#E8C766"; ctx.fill();
    ctx.strokeStyle = "#B8922E"; ctx.lineWidth = 3; ctx.stroke();
    ctx.fillStyle = "#4A3708"; ctx.font = "800 34px Inter";
    ctx.fillText("★  WEEKLY CHAMPION", 186, 434);
  }
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 300px Inter";
  ctx.fillText(`${score}/${total}`, 150, 680);
  const size = 150, gap = 36;
  marks.forEach((ok, i) => {
    const x = 150 + i * (size + gap);
    roundRect(ctx, x, 760, size, size, 32);
    ctx.fillStyle = ok ? "#E5EEE8" : "#F5E4DF"; ctx.fill();
    ctx.strokeStyle = ok ? "#FFFFFF" : "#9E4535"; ctx.lineWidth = 10;
    ctx.beginPath();
    if (ok) { ctx.moveTo(x + 40, y(80)); ctx.lineTo(x + 66, y(106)); ctx.lineTo(x + 112, y(48)); ctx.strokeStyle = "#2D4A3E"; }
    else { ctx.moveTo(x + 48, y(40)); ctx.lineTo(x + 102, y(110)); ctx.moveTo(x + 102, y(40)); ctx.lineTo(x + 48, y(110)); }
    ctx.stroke();
  });
  function y(v) { return 760 + v; }
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "600 52px Inter";
  ctx.fillText(score === total ? "Flawless. Can you match it?" : "Think you can beat me?", 90, 1120);
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = "500 40px Inter";
  ctx.fillText("Play free at curiovault.co/trivia", 90, 1200);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

export const ShareScore = ({ score, total, marks, date, streak = 0, label = "Daily Three", champion = false }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const iso = date || new Date().toISOString().slice(0, 10);
  const dateLabel = new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  const text = `${champion ? "👑 Weekly Champion · " : ""}CurioVault ${label} · ${dateLabel}\n${score}/${total} ${marks.map((m) => (m ? "🟩" : "🟥")).join("")}${streak > 1 ? ` · ${streak}-day streak` : ""}\nPlay free: https://curiovault.co/trivia`;

  const share = async () => {
    setBusy(true);
    try {
      const blob = await drawCard({ score, total, marks, dateLabel, streak, label, champion });
      const file = new File([blob], `curiovault-daily-${iso}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text, title: `CurioVault ${label}` }); } catch { /* user cancelled */ }
      } else {
        setPreview({ url: URL.createObjectURL(blob), name: file.name });
      }
    } finally { setBusy(false); }
  };

  const close = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };
  const Pill = "inline-flex items-center justify-center gap-2 font-ui font-semibold text-sm rounded-full px-5 py-2.5 transition-[transform,background-color] active:scale-[0.98]";

  return (
    <>
      <button data-testid="share-score-btn" onClick={share} disabled={busy} className={`${Pill} bg-[#2D4A3E] text-white hover:bg-[#243D33] disabled:opacity-60`}>
        <Share2 className="w-4 h-4" /> {busy ? "Preparing…" : "Share score"}
      </button>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="share-modal">
          <div className="bg-[#F4F1EA] rounded-[20px] p-4 sm:p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-ui font-semibold text-sm text-[#161514]">Share your {label}</p>
              <button onClick={close} data-testid="share-modal-close" className="text-[#77716A] hover:text-[#161514]"><X className="w-4 h-4" /></button>
            </div>
            <img src={preview.url} alt="Daily Three score card" data-testid="share-card-image" className="w-full rounded-[14px] border border-[#DDD5C7]" />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a href={preview.url} download={preview.name} data-testid="share-download" className={`${Pill} bg-[#161514] text-white hover:bg-black`}><Download className="w-4 h-4" /> Download</a>
              <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className={`${Pill} bg-[#2D4A3E] text-white hover:bg-[#243D33]`}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
            <button data-testid="share-copy" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copied, paste it anywhere"))} className={`${Pill} w-full mt-2 bg-white border border-[#DDD5C7] text-[#161514] hover:bg-[#F7F4EE]`}><Copy className="w-4 h-4" /> Copy text</button>
            <p className="font-ui text-[11px] text-[#77716A] mt-3 text-center">For Instagram Stories, download the card and add it from your photos.</p>
          </div>
        </div>
      )}
    </>
  );
};
